import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import emailjs from '@emailjs/browser';
import { API, EMAILJS_CONFIG } from '@/lib/data';
import { useSettings } from '@/lib/useSettings';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, Clock, Phone, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';

export const ContactForm = ({ compact = false }) => {
  const settings = useSettings();
  const CONTACT_INFO = settings.contact || {};
  const [form, setForm] = useState({ ad_soyad: '', telefon: '', eposta: '', konu: '', mesaj: '', kvkk_consent: false, website: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.ad_soyad.trim()) return 'Ad Soyad alanı zorunludur.';
    if (!form.telefon.trim()) return 'Telefon alanı zorunludur.';
    if (!form.mesaj.trim()) return 'Mesaj alanı zorunludur.';
    if (!form.kvkk_consent) return 'KVKK onayı gereklidir.';
    return null;
  };

  const sendEmailNotification = async () => {
    const { service_id, template_id, public_key } = EMAILJS_CONFIG;
    const notification_email = settings.contact?.email || '';
    if (!service_id || !template_id || !public_key) return;

    try {
      await emailjs.send(service_id, template_id, {
        from_name: form.ad_soyad,
        from_phone: form.telefon,
        from_email: form.eposta || 'Belirtilmedi',
        subject: form.konu || 'Genel',
        message: form.mesaj,
        to_email: notification_email,
      }, public_key);
    } catch (err) {
      // Silent fail - don't block form submission
    }
  };

  const checkRateLimit = () => {
    const key = 'contact_submissions';
    const now = Date.now();
    const hour = 60 * 60 * 1000;
    const stored = JSON.parse(localStorage.getItem(key) || '[]');
    const recent = stored.filter(t => now - t < hour);
    if (recent.length >= 3) return false;
    return true;
  };

  const recordSubmission = () => {
    const key = 'contact_submissions';
    const now = Date.now();
    const hour = 60 * 60 * 1000;
    const stored = JSON.parse(localStorage.getItem(key) || '[]');
    const recent = stored.filter(t => now - t < hour);
    recent.push(now);
    localStorage.setItem(key, JSON.stringify(recent));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setErrorMsg(err);
      setStatus('error');
      return;
    }
    if (!checkRateLimit()) {
      setErrorMsg('Saatte en fazla 3 mesaj gönderebilirsiniz. Lütfen daha sonra tekrar deneyin.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact`, form);
      sendEmailNotification();
      recordSubmission();
      setStatus('success');
      setForm({ ad_soyad: '', telefon: '', eposta: '', konu: '', mesaj: '', kvkk_consent: false, website: '' });
    } catch (e) {
      setStatus('error');
      setErrorMsg(e.response?.data?.detail || 'Bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="contact-form-success">
        <div className="w-14 h-14 rounded-full bg-[#1F6E7A]/10 flex items-center justify-center mb-4">
          <CheckCircle size={28} className="text-[#1F6E7A]" />
        </div>
        <h3 className="font-heading font-semibold text-[24px] mb-2">Mesajınız Gönderildi</h3>
        <p className="text-[14px] text-[#5F6B76] max-w-sm">
          En kısa sürede size dönüş yapacağız. Teşekkür ederiz.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-4 px-5 py-2 rounded-full text-sm font-medium text-[#1F6E7A] border border-[#1F6E7A] hover:bg-[#1F6E7A]/5 transition-colors"
        >
          Yeni Mesaj Gönder
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
      {/* Honeypot - hidden from humans, visible to bots */}
      <div className="absolute opacity-0 -z-10 pointer-events-none" aria-hidden="true">
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => handleChange('website', e.target.value)} />
      </div>
      <div className={compact ? 'space-y-3' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
        <div className={compact ? '' : 'sm:col-span-1'}>
          <label className="block text-[14px] font-medium text-[#5F6B76] mb-1.5">Ad Soyad *</label>
          <input
            type="text"
            value={form.ad_soyad}
            onChange={(e) => handleChange('ad_soyad', e.target.value)}
            className="w-full px-4 py-3 rounded-2xl border border-[#E7E5E4] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1F6E7A] focus:border-transparent transition-shadow"
            placeholder="Adınız Soyadınız"
            data-testid="contact-form-name-input"
          />
        </div>
        <div>
          <label className="block text-[14px] font-medium text-[#5F6B76] mb-1.5">Telefon *</label>
          <input
            type="tel"
            value={form.telefon}
            onChange={(e) => handleChange('telefon', e.target.value)}
            className="w-full px-4 py-3 rounded-2xl border border-[#E7E5E4] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1F6E7A] focus:border-transparent transition-shadow"
            placeholder="+90 5XX XXX XX XX"
            data-testid="contact-form-phone-input"
          />
        </div>
        {!compact && (
          <>
            <div>
              <label className="block text-[14px] font-medium text-[#5F6B76] mb-1.5">E-posta</label>
              <input
                type="email"
                value={form.eposta}
                onChange={(e) => handleChange('eposta', e.target.value)}
                className="w-full px-4 py-3 rounded-2xl border border-[#E7E5E4] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1F6E7A] focus:border-transparent transition-shadow"
                placeholder="email@ornek.com"
                data-testid="contact-form-email-input"
              />
            </div>
            <div>
              <label className="block text-[14px] font-medium text-[#5F6B76] mb-1.5">Konu</label>
              <input
                type="text"
                value={form.konu}
                onChange={(e) => handleChange('konu', e.target.value)}
                className="w-full px-4 py-3 rounded-2xl border border-[#E7E5E4] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1F6E7A] focus:border-transparent transition-shadow"
                placeholder="Görüşme konusu"
                data-testid="contact-form-subject-input"
              />
            </div>
          </>
        )}
      </div>
      <div>
        <label className="block text-[14px] font-medium text-[#5F6B76] mb-1.5">Mesaj *</label>
        <textarea
          value={form.mesaj}
          onChange={(e) => handleChange('mesaj', e.target.value)}
          rows={compact ? 3 : 4}
          className="w-full px-4 py-3 rounded-2xl border border-[#E7E5E4] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1F6E7A] focus:border-transparent transition-shadow resize-none"
          placeholder="Mesajınızı yazın..."
          data-testid="contact-form-message-textarea"
        />
      </div>
      <div className="flex items-start gap-2">
        <Checkbox
          id="kvkk"
          checked={form.kvkk_consent}
          onCheckedChange={(v) => handleChange('kvkk_consent', v)}
          className="mt-0.5"
          data-testid="contact-form-kvkk-checkbox"
        />
        <label htmlFor="kvkk" className="text-[14px] text-[#5F6B76] leading-relaxed cursor-pointer">
          <Link to="/kvkk" className="text-[#1F6E7A] underline" target="_blank">KVKK Aydınlatma Metni</Link>'ni okudum, kişisel verilerimin işlenmesini kabul ediyorum. *
        </label>
      </div>
      {status === 'error' && errorMsg && (
        <div className="flex items-center gap-2 text-red-600 text-[14px]" data-testid="contact-form-error-text">
          <AlertCircle size={14} />
          {errorMsg}
        </div>
      )}
      <button
        type="submit"
        disabled={!form.kvkk_consent || status === 'loading'}
        className="w-full py-3 rounded-full bg-[#1F6E7A] text-white text-[14px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#185960] transition-colors shadow-[0_8px_24px_-12px_rgba(31,110,122,0.4)]"
        data-testid="contact-form-submit-button"
      >
        {status === 'loading' ? 'Gönderiliyor...' : 'Mesaj Gönder'}
      </button>
      {!compact && (
        <p className="text-center text-xs text-[#5F6B76]">
          veya{' '}
          <a href={`https://wa.me/${CONTACT_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-[#1F6E7A] font-medium">
            WhatsApp'ı tercih edin
          </a>
        </p>
      )}
    </form>
  );
};
